import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";

import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";

import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  infoText: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    marginTop: theme.spacing(1),
    lineHeight: 1.5,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    padding: "6px 0",
  },
  chipActive: {
    backgroundColor: "#d4edda",
    color: "#155724",
  },
  chipInactive: {
    backgroundColor: "#fff3cd",
    color: "#856404",
  },
  error: {
    color: "#c62828",
    marginTop: theme.spacing(1),
  },
  success: {
    color: "#2e7d32",
    marginTop: theme.spacing(1),
  },
  field: {
    marginTop: theme.spacing(2),
  },
  sendButton: {
    marginTop: theme.spacing(2),
  },
});

const hebrewLabels = {
  title: "שליחת דואר הזמנות",
  description:
    "אישור ההזמנה נשלח בדקה שאחרי התשלום, בנפרד מדף התודה. המתג הזה בוחר אם הדואר יוצא דרך Gmail או דרך Amazon SES. אותה בחירה חלה על שני שרתי האתר.",
  sesSwitch: "שליחה דרך Amazon SES",
  gmailActive: "Gmail פעיל",
  sesActive: "Amazon SES פעיל",
  activeFrom: "שולח בפועל",
  sesFrom: "שולח SES",
  region: "אזור SES",
  credentials: "מפתחות AWS על השרת",
  credentialsReady: "מוגדרים",
  credentialsMissing: "חסרים",
  credentialsHint:
    "המפתחות נשארים במשתני הסביבה של השרת (AWS_ACCESS_KEY, AWS_SECRET_KEY). כתובת השולח חייבת להיות מאומתת ב-SES באזור שמופיע כאן.",
  saved: "ההגדרה נשמרה",
  loading: "טוען…",
  testTitle: "בדיקת דואר הזמנה דרך SES",
  testDescription:
    "שולח את תבנית אישור ההזמנה ללקוח רק לכתובת שתזין. השליחה תמיד עוברת ב-SES, גם כשהמתג למעלה עדיין על Gmail.",
  testTo: "כתובת לקבלה",
  testOrderId: "מספר הזמנה (רשות)",
  testOrderHint: "השאר ריק כדי לשלוח הזמנת דוגמה",
  testSend: "שלח דואר בדיקה",
  testSending: "שולח…",
};

class MailTransportManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      saving: false,
      error: null,
      successMsg: null,
      config: null,
      testTo: "",
      testOrderId: "",
      testSending: false,
      testError: null,
      testSuccess: null,
    };
  }

  componentDidMount() {
    this.fetchConfig();
  }

  fetchConfig = async () => {
    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/mail-transport`, {
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to load");
      this.setState({ config: data.config, loading: false });
    } catch (err) {
      this.setState({ error: err.message, loading: false });
    }
  };

  handleTestField = (field) => (event) => {
    this.setState({ [field]: event.target.value, testError: null, testSuccess: null });
  };

  handleTestSend = async () => {
    const { testTo, testOrderId } = this.state;
    this.setState({ testSending: true, testError: null, testSuccess: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/mail-transport/test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
        body: JSON.stringify({
          to: testTo.trim(),
          orderId: testOrderId.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to send");
      this.setState({
        testSending: false,
        testSuccess: `נשלח אל ${data.to}. מזהה הודעה: ${data.messageId || "—"}`,
      });
    } catch (err) {
      this.setState({ testError: err.message, testSending: false });
    }
  };

  handleSwitch = async (event) => {
    const provider = event.target.checked ? "ses" : "gmail";
    this.setState({ saving: true, error: null, successMsg: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/mail-transport`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
        body: JSON.stringify({ provider }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to save");
      this.setState({
        config: data.config,
        saving: false,
        successMsg: hebrewLabels.saved,
      });
    } catch (err) {
      this.setState({ error: err.message, saving: false });
    }
  };

  render() {
    const { classes } = this.props;
    const {
      loading,
      saving,
      error,
      successMsg,
      config,
      testTo,
      testOrderId,
      testSending,
      testError,
      testSuccess,
    } = this.state;
    const sesOn = config?.provider === "ses";
    const sesReady = Boolean(config?.ses?.configured);

    return (
      <div className={classes.root}>
        <Paper className={classes.section}>
          <Typography variant="h6">{hebrewLabels.title}</Typography>
          <Typography className={classes.infoText}>
            {hebrewLabels.description}
          </Typography>

          {loading && <CircularProgress size={28} style={{ marginTop: 16 }} />}

          {config && (
            <div>
              <FormControlLabel
                style={{ marginTop: 16 }}
                control={
                  <Switch
                    checked={sesOn}
                    onChange={this.handleSwitch}
                    disabled={saving}
                    color="primary"
                  />
                }
                label={hebrewLabels.sesSwitch}
              />
              <div style={{ marginTop: 8 }}>
                <Chip
                  label={sesOn ? hebrewLabels.sesActive : hebrewLabels.gmailActive}
                  className={sesOn ? classes.chipActive : classes.chipInactive}
                />
              </div>
              <div className={classes.row}>
                <span>{hebrewLabels.activeFrom}</span>
                <span>{config.fromAddress}</span>
              </div>
              <div className={classes.row}>
                <span>{hebrewLabels.sesFrom}</span>
                <span>{config.ses?.fromAddress}</span>
              </div>
              <div className={classes.row}>
                <span>{hebrewLabels.region}</span>
                <span>{config.ses?.region}</span>
              </div>
              <div className={classes.row}>
                <span>{hebrewLabels.credentials}</span>
                <span>
                  {config.ses?.configured
                    ? hebrewLabels.credentialsReady
                    : hebrewLabels.credentialsMissing}
                </span>
              </div>
              <Typography className={classes.infoText}>
                {hebrewLabels.credentialsHint}
              </Typography>
              {sesOn && config.ses && !config.ses.configured && (
                <Typography className={classes.error}>
                  SES פעיל, אבל מפתחות ה-AWS חסרים על השרת. דואר הזמנות יישאר בתור עד שהמפתחות יוגדרו.
                </Typography>
              )}
            </div>
          )}

          {error && <Typography className={classes.error}>{error}</Typography>}
          {successMsg && (
            <Typography className={classes.success}>{successMsg}</Typography>
          )}
        </Paper>

        <Paper className={classes.section}>
          <Typography variant="h6">{hebrewLabels.testTitle}</Typography>
          <Typography className={classes.infoText}>
            {hebrewLabels.testDescription}
          </Typography>
          <TextField
            className={classes.field}
            label={hebrewLabels.testTo}
            value={testTo}
            onChange={this.handleTestField("testTo")}
            fullWidth
            type="email"
            variant="outlined"
          />
          <TextField
            className={classes.field}
            label={hebrewLabels.testOrderId}
            value={testOrderId}
            onChange={this.handleTestField("testOrderId")}
            helperText={hebrewLabels.testOrderHint}
            fullWidth
            variant="outlined"
          />
          <Button
            className={classes.sendButton}
            variant="contained"
            color="primary"
            onClick={this.handleTestSend}
            disabled={testSending || loading || !sesReady || !testTo.trim()}
          >
            {testSending ? hebrewLabels.testSending : hebrewLabels.testSend}
          </Button>
          {!loading && config && !sesReady && (
            <Typography className={classes.error}>
              אי אפשר לשלוח בדיקה לפני שמפתחות ה-AWS מוגדרים על השרת.
            </Typography>
          )}
          {testError && <Typography className={classes.error}>{testError}</Typography>}
          {testSuccess && (
            <Typography className={classes.success}>{testSuccess}</Typography>
          )}
        </Paper>
      </div>
    );
  }
}

export default withStyles(styles)(MailTransportManager);
